import {
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
  Input as InputIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { FieldOrigin } from "./fieldOrigin.js";

type Props =
  | {
      // A hint line under a field (the default).
      variant?: "line";
      origin: FieldOrigin;
      // Field-specific copy for a value taken from the quote, and for a field
      // the quote had nothing for. Both are omitted for a field the quote can
      // never fill: it then shows nothing until it is manually changed.
      takenText?: string;
      absentText?: string;
      // An "absent" field that needs input reads as information, not as a
      // plain dash.
      absentIcon?: "dash" | "info";
      // Rendered after the copy of a "taken" or "absent" hint (e.g. a link
      // that acts on the value); a changed field shows Reset instead.
      trailing?: React.ReactNode;
      // Names the field in the Reset button's accessible name.
      fieldLabel: string;
      onReset: () => void;
      disabled?: boolean;
    }
  | {
      // A compact marker next to a checkbox label: a checkbox is never taken
      // from the quote, so only a ticked ("changed") one shows anything.
      variant: "marker";
      origin: FieldOrigin;
      fieldLabel: string;
      onReset: () => void;
      disabled?: boolean;
    };

// Provenance under a quick-accept field: taken from the shared quote, not
// contained in it, or manually changed (with Reset to the prefill).
export function FieldOriginHint(props: Props) {
  const { origin, fieldLabel, onReset, disabled = false } = props;
  const { t } = useTranslate(["ProjectCreate", "Global"]);

  const iconSx = {
    fontSize: 16,
    color: origin === "changed" ? "warning.main" : "text.secondary",
  };

  const resetButton = (
    <Button
      variant="text"
      size="small"
      onClick={onReset}
      disabled={disabled}
      aria-label={t("Reset {field}", {
        ns: "ProjectCreate",
        field: fieldLabel,
      })}
      sx={{ py: 0, minWidth: 0 }}
    >
      {t("Reset", { ns: "Global" })}
    </Button>
  );

  if (props.variant === "marker") {
    if (origin !== "changed") return null;
    return (
      <Stack
        direction="row"
        spacing={0.75}
        sx={{ alignItems: "center", display: "inline-flex" }}
      >
        <EditIcon sx={iconSx} />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("Manually changed", { ns: "ProjectCreate" })}
        </Typography>
        {resetButton}
      </Stack>
    );
  }

  const text =
    origin === "changed"
      ? t("Manually changed", { ns: "ProjectCreate" })
      : origin === "taken"
        ? props.takenText
        : props.absentText;
  if (text === undefined) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{ alignItems: "center", minHeight: 24, px: 1.75, mt: 0.5 }}
    >
      {origin === "changed" ? (
        <EditIcon sx={iconSx} />
      ) : origin === "taken" ? (
        <InputIcon sx={iconSx} />
      ) : props.absentIcon === "info" ? (
        <InfoOutlinedIcon sx={iconSx} />
      ) : (
        <RemoveIcon sx={iconSx} />
      )}
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", flexGrow: 1 }}
      >
        {text}
        {origin !== "changed" && props.trailing}
      </Typography>
      {origin === "changed" && resetButton}
    </Stack>
  );
}
